import { createUseStyles } from 'react-jss';
import { useMemo } from 'react';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import {
  IntegrationAccount,
  IntegrationCapability,
  ProviderEnum,
  Calendar,
  useUpdateCalendar,
  useReconnectToAccount,
  useDeleteIntegration,
} from '@app/api';
import { Button, Chip, SquareButton, TooltipText, useToast } from '@app/core';
import classNames from 'classnames';

const providerIcons: Record<ProviderEnum, string> = {
  google: 'ri-google-fill',
  microsoft: 'ri-windows-fill',
  cronofy: 'ri-calendar-2-line',
};

const useStyles = createUseStyles((theme: Theme) => ({
  profile: {
    padding: theme.marginBase * 2,
    border: {
      width: 1,
      color: theme.colors.grayBorder,
      style: 'solid',
    },
    borderRadius: theme.borderRadius.std,
    marginBottom: theme.marginBase * 2,
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  profileHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  profileName: {
    ...theme.fonts.h5,
  },
  calendarList: {
    marginTop: theme.marginBase * 2,
  },
  calendar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase,
    gap: theme.marginBase,
  },
  calendarContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.grayBackground,
    padding: [[theme.marginBase, theme.marginBase * 2]],
  },
  calendarDisabled: {
    opacity: 0.5,
  },
  calendarName: {
    ...theme.fonts.label,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  videoIcon: {
    fontSize: theme.iconSize.small,
  },
  providerIcon: {
    fontSize: theme.iconSize.small,
  },
}));

interface Props {
  account: IntegrationAccount;
}

export const IntegrationAccountCard = ({ account }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const { mutate: updateCalendar } = useUpdateCalendar();
  const toastActions = {
    onSuccess: () => toast.saved(),
    onError: (e?: Error) => toast.exception(e),
  };
  const showCalendar = (id: string) =>
    updateCalendar({ id, isVisible: true }, toastActions);
  const hideCalendar = (id: string) =>
    updateCalendar({ id, isVisible: false }, toastActions);

  const reconnect = useReconnectToAccount(account.id);

  const { mutate: deleteIntegration } = useDeleteIntegration(account.id);

  const calendars = useMemo(() => {
    const cal = [...(account.calendars || [])];

    const getScore = (a: Calendar) => {
      let score = a.isPrimary ? 0b10 : 0;
      score += a.isReadonly ? 0 : 0b01;
      return score;
    };

    cal.sort((a, b) => {
      return getScore(b) - getScore(a);
    });
    return cal;
  }, [account.calendars]);

  return (
    <div className={classes.profile}>
      <div className={classes.profileHeader}>
        <div className={classes.profileHeaderTitle}>
          {!account.connected && (
            <Chip color="red">
              {t('settings.toolIntegrations.calendar.profile.disconnected')}
            </Chip>
          )}
          <h4 className={classes.profileName}>{account.name}</h4>
          {account.provider && providerIcons[account.provider] ? (
            <TooltipText title={account.provider} requireDiv>
              <Chip color="ink">
                <i
                  className={classNames(
                    providerIcons[account.provider],
                    classes.providerIcon,
                  )}
                />
              </Chip>
            </TooltipText>
          ) : (
            <Chip color="ink">{account.provider}</Chip>
          )}
        </div>

        {!account.connected ? (
          <Button small onClick={reconnect} variant="muted">
            {t('settings.toolIntegrations.calendar.profile.reconnect')}
          </Button>
        ) : (
          (account.capabilities & IntegrationCapability.CALENDAR) !== 0 && (
            <Button
              small
              target="_blank"
              variant="transparent"
              color="red"
              onClick={() => {
                deleteIntegration(undefined, {
                  onSuccess: () =>
                    toast.success(
                      'settings.toolIntegrations.calendar.profile.deleted',
                    ),
                  onError: (e) => toast.exception(e),
                });
              }}
            >
              {t('settings.toolIntegrations.calendar.profile.delete')}
            </Button>
          )
        )}
      </div>
      <ul className={classes.calendarList}>
        {calendars.map((calendar) => (
          <li
            className={classNames(classes.calendar, {
              [classes.calendarDisabled]:
                !account.accountId || !calendar.isVisible,
            })}
          >
            <div className={classes.calendarContent}>
              <h5 className={classes.calendarName}>{calendar.name}</h5>
              <div className={classes.chips}>
                {calendar.integratedConferencingServiceAvailable && (
                  <TooltipText
                    requireDiv
                    title={t(
                      'settings.toolIntegrations.calendar.integratedConferencingServiceAvailable',
                    )}
                  >
                    <Chip color="yellow">
                      <i
                        className={classNames(
                          'ri-vidicon-line',
                          classes.videoIcon,
                        )}
                      />
                    </Chip>
                  </TooltipText>
                )}
                {!!calendar.isPrimary && (
                  <Chip color="blue">
                    {t('settings.toolIntegrations.calendar.isPrimary')}
                  </Chip>
                )}
              </div>
            </div>

            {calendar.isVisible ? (
              <SquareButton
                color="blue"
                icon="ri-eye-line"
                onClick={() => hideCalendar(calendar.id)}
              />
            ) : (
              <SquareButton
                color="ink"
                icon="ri-eye-off-line"
                onClick={() => showCalendar(calendar.id)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
